import React, { useState, useCallback, useRef, useMemo } from 'react';
import {
  Drawer,
  List,
  ListItemText,
  ListItemIcon,
  Collapse,
  useTheme,
  Box,
  ListItemButton,
  Typography,
  Divider,
  Tooltip,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Fade,
  Zoom,
  useMediaQuery,
  Switch,
  Menu as MuiMenu,
  MenuItem,
  Badge,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  Search,
  Clear,
  Menu,
  Close,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  NotificationsNone as NotificationsNoneIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import sideBarItems from 'Constants/sidebarItem';
import UserDetail from '../UserDetail';
import { useAssignments, needsAction } from 'contexts/AssignmentsContext';
import useCredentials from 'hooks/useCredentials';
import { useThemeContext } from '../../contexts/ThemeContext';
import './sidebarStyles.css';
import { COMPANY_ROLES } from 'Constants/roles';
import useUserDetails from 'hooks/useUserDetails';

// small red count pill for menu items that carry a badge
const CountPill = ({ count }) =>
  count > 0 ? (
    <Box
      component="span"
      sx={{
        minWidth: 18,
        height: 18,
        px: 0.5,
        ml: 1,
        borderRadius: '999px',
        background: '#ef4444',
        color: '#fff',
        fontSize: 11,
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {count > 99 ? '99+' : count}
    </Box>
  ) : null;
const NewSideBar = React.memo(() => {
  const renderCount = useRef(0);
  renderCount.current += 1;

  const [opened, setIsOpened] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { userData: user } = useCredentials();
  const { companyId } = useUserDetails();
  const roles = useMemo(() => new Set(user?.roles || []), [user?.roles]);

  // translator assignments — powers the badges and the notification bell
  const { assignments, actionNeeded, refresh: refreshAssignments } = useAssignments();
  const [bellAnchor, setBellAnchor] = useState(null);
  const actionItems = useMemo(() => assignments.filter(needsAction), [assignments]);
  const badgeCountFor = useCallback(
    (item) => (item.badge === 'assignments' ? actionNeeded : 0),
    [actionNeeded]
  );
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { mode, toggleMode } = useThemeContext();
  // Auto-expand parent when child is active
  const autoExpandParents = useCallback(() => {
    const newOpened = {};
    sideBarItems.forEach((item) => {
      if (item.children) {
        const hasActiveChild = item.children.some((child) => location.pathname === child.path);
        if (hasActiveChild) {
          newOpened[item.path] = true;
        }
      }
    });
    setIsOpened((prev) => ({ ...prev, ...newOpened }));
  }, [location.pathname]);

  // Auto-expand on mount and path change
  React.useEffect(() => {
    autoExpandParents();
  }, [autoExpandParents]);

  const toggleCollapse = useCallback((segment) => {
    setIsOpened((prevOpened) => ({
      ...prevOpened,
      [segment]: !prevOpened[segment],
    }));
  }, []);

  const handleItemClick = useCallback(
    async (path) => {
      try {
        setIsLoading(true);
        // Simulate a small delay for better UX
        //await new Promise((resolve) => setTimeout(resolve, 100));
        navigate(path);
        if (isMobile) {
          setIsMobileOpen(false);
        }
      } catch (error) {
        console.error('Navigation error:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, isMobile]
  );

  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  // Filter items based on search term and roles
  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) {
      return sideBarItems.filter((item) => item.access.some((element) => roles.has(element)));
    }

    const searchLower = searchTerm.toLowerCase();
    return sideBarItems.filter((item) => {
      // Check if user has access
      if (!item.access.some((element) => roles.has(element))) {
        return false;
      }

      // Check if item name matches search
      if (item.name.toLowerCase().includes(searchLower)) {
        return true;
      }

      // Check if any children match search
      if (item.children) {
        return item.children.some(
          (child) =>
            child.access.some((element) => roles.has(element)) &&
            child.name.toLowerCase().includes(searchLower)
        );
      }

      return false;
    });
  }, [sideBarItems, roles, searchTerm]);

  const renderListItems = useCallback(
    (items) =>
      items.map((item, index) => {
        // Check if the item's role includes the current role
        if (!item.access.some((element) => roles.has(element))) {
          return null;
        }

        const isSelected = location.pathname === item.path;
        const hasActiveChild = item.children?.some((child) => location.pathname === child.path);
        const isExpanded = opened[item.path];

        return (
          <Fade in={true} timeout={300 + index * 50} key={index}>
            <div>
              <Tooltip title={item.name} placement="right" TransitionComponent={Zoom} arrow>
                <ListItemButton
                  onClick={() => {
                    if (item.children) {
                      toggleCollapse(item.path);
                    } else {
                      handleItemClick(item.path);
                    }
                  }}
                  selected={isSelected || hasActiveChild}
                  className={`sidebar-item ${isSelected || hasActiveChild ? 'active' : ''}`}
                  sx={{
                    margin: '4px 8px',
                    borderRadius: '8px',
                    minHeight: '48px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                      transform: 'translateX(4px)',
                    },
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.main + '20',
                      color: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: theme.palette.primary.main + '30',
                      },
                      '& .MuiListItemIcon-root': {
                        color: theme.palette.primary.main,
                      },
                    },
                    '& .MuiListItemIcon-root': {
                      minWidth: '40px',
                      color: theme.palette.text.secondary,
                    },
                    '& .MuiListItemText-primary': {
                      fontWeight: isSelected || hasActiveChild ? 600 : 400,
                      fontSize: '0.875rem',
                    },
                  }}
                >
                  <ListItemIcon>
                    {React.cloneElement(item.img, {
                      sx: { fontSize: '1.25rem' },
                    })}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    primaryTypographyProps={{
                      noWrap: true,
                    }}
                  />
                  <CountPill count={badgeCountFor(item)} />
                  {item.children && (
                    <ExpandLess
                      sx={{
                        transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
                        transition: 'transform 0.2s ease-in-out',
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>

              {item.children && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit={false}>
                  <List component="div" disablePadding>
                    {item.children.map((child, childIndex) => {
                      if (!child.access.some((element) => roles.has(element))) {
                        return null;
                      }

                      const isChildSelected = location.pathname === child.path;

                      return (
                        <Zoom in={isExpanded} timeout={200 + childIndex * 50} key={childIndex}>
                          <Tooltip
                            title={child.name}
                            placement="right"
                            TransitionComponent={Zoom}
                            arrow
                          >
                            <ListItemButton
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleItemClick(child.path);
                              }}
                              selected={isChildSelected}
                              className={`sidebar-item ${isChildSelected ? 'active' : ''}`}
                              sx={{
                                pl: 6,
                                margin: '2px 8px',
                                borderRadius: '6px',
                                minHeight: '40px',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                  backgroundColor: theme.palette.action.hover,
                                  transform: 'translateX(4px)',
                                },
                                '&.Mui-selected': {
                                  backgroundColor: theme.palette.primary.main + '20',
                                  color: theme.palette.primary.main,
                                  '&:hover': {
                                    backgroundColor: theme.palette.primary.main + '30',
                                  },
                                  '& .MuiListItemIcon-root': {
                                    color: theme.palette.primary.main,
                                  },
                                },
                                '& .MuiListItemIcon-root': {
                                  minWidth: '32px',
                                  color: theme.palette.text.secondary,
                                },
                                '& .MuiListItemText-primary': {
                                  fontWeight: isChildSelected ? 600 : 400,
                                  fontSize: '0.8rem',
                                },
                              }}
                            >
                              <ListItemIcon>
                                {React.cloneElement(child.img, {
                                  sx: { fontSize: '1.1rem' },
                                })}
                              </ListItemIcon>
                              <ListItemText
                                primary={child.name}
                                primaryTypographyProps={{
                                  noWrap: true,
                                }}
                              />
                              <CountPill count={badgeCountFor(child)} />
                            </ListItemButton>
                          </Tooltip>
                        </Zoom>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </div>
          </Fade>
        );
      }),
    [location.pathname, opened, toggleCollapse, handleItemClick, theme, roles, badgeCountFor]
  );

  const drawerContent = (
    <>
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <UserDetail />
        </Box>
        <Tooltip title="My assignments" arrow>
          <IconButton
            onClick={(e) => {
              setBellAnchor(e.currentTarget);
              refreshAssignments();
            }}
            sx={{ color: theme.palette.text.secondary }}
          >
            <Badge badgeContent={actionNeeded} color="error" max={99}>
              <NotificationsNoneIcon />
            </Badge>
          </IconButton>
        </Tooltip>
      </Box>

      {/* Notification dropdown */}
      <MuiMenu
        anchorEl={bellAnchor}
        open={Boolean(bellAnchor)}
        onClose={() => setBellAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 320, maxHeight: 400, borderRadius: '12px', mt: 0.5 } }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: theme.palette.text.primary }}>
            Assignments needing action
          </Typography>
        </Box>

        {actionItems.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 13.5, color: theme.palette.text.secondary }}>
              You're all caught up 🎉
            </Typography>
          </Box>
        ) : (
          actionItems.slice(0, 6).map((a) => (
            <MenuItem
              key={a._id}
              onClick={() => {
                setBellAnchor(null);
                navigate('/my-assignments');
              }}
              sx={{
                py: 1.25,
                borderBottom: `1px solid ${theme.palette.divider}`,
                whiteSpace: 'normal',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{ fontSize: 13.5, fontWeight: 600, color: theme.palette.text.primary }}
                >
                  {a.languageName} · {a.task?.taskName || 'Task'}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: a.status === 'rejected' ? '#dc2626' : '#d97706',
                  }}
                >
                  {a.status === 'rejected'
                    ? 'Sent back — needs re-upload'
                    : 'Awaiting your translation'}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}

        <MenuItem
          onClick={() => {
            setBellAnchor(null);
            navigate('/my-assignments');
          }}
          sx={{
            justifyContent: 'center',
            py: 1.25,
            color: theme.palette.primary.main,
            fontWeight: 600,
            fontSize: 13.5,
          }}
        >
          View all assignments
        </MenuItem>
      </MuiMenu>

      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search menu..."
          value={searchTerm}
          onChange={handleSearchChange}
          className="sidebar-search"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: theme.palette.text.secondary }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={clearSearch}
                  sx={{ color: theme.palette.text.secondary }}
                >
                  <Clear />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />
      </Box>

      <Divider />

      <Box sx={{ flex: 1, overflow: 'auto' }} className="sidebar-scrollbar">
        {filteredItems.length > 0 ? (
          <List sx={{ py: 1 }}>{renderListItems(filteredItems)}</List>
        ) : (
          <Box
            sx={{
              p: 3,
              textAlign: 'center',
              color: theme.palette.text.secondary,
            }}
          >
            <Typography variant="body2">No menu items found for "{searchTerm}"</Typography>
          </Box>
        )}
      </Box>

      {searchTerm && (
        <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Chip
            label={`${filteredItems.length} item${filteredItems.length !== 1 ? 's' : ''} found`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
      )}

      {/* Dark Mode Toggle */}
      {/* {companyId !== COMPANY_ROLES.SATIN_FINSERV_LTD && (
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            position: 'sticky',
            bottom: 0,
            background: theme.palette.background.paper,
            zIndex: 2,
          }}
        >
          <LightModeIcon
            sx={{
              color: mode === 'dark' ? theme.palette.text.disabled : theme.palette.warning.main,
              fontSize: 20,
            }}
          />
          <Switch
            checked={mode === 'dark'}
            onChange={toggleMode}
            color="primary"
            inputProps={{ 'aria-label': 'toggle dark mode' }}
          />
          <DarkModeIcon
            sx={{
              color: mode === 'dark' ? theme.palette.info.main : theme.palette.text.disabled,
              fontSize: 20,
            }}
          />
          <Typography
            variant="body2"
            sx={{ ml: 1, color: theme.palette.text.secondary, fontWeight: 500 }}
          >
            {mode === 'dark' ? 'Dark' : 'Light'} Mode
          </Typography>
        </Box>
      )} */}
    </>
  );

  return (
    <>
      {/* Mobile menu button */}
      {isMobile && (
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: theme.zIndex.drawer + 1,
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[4],
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          {isMobileOpen ? <Close /> : <Menu />}
        </IconButton>
      )}

      {/* Desktop Drawer */}
      {!isMobile && (
        <Drawer
          sx={{
            width: 280,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 280,
              boxSizing: 'border-box',
              borderRight: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.default,
            },
          }}
          variant="permanent"
          anchor="left"
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <>
          {/* Backdrop */}
          {isMobileOpen && (
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: theme.zIndex.drawer,
                backdropFilter: 'blur(4px)',
              }}
              onClick={() => setIsMobileOpen(false)}
            />
          )}

          <Drawer
            variant="temporary"
            anchor="left"
            open={isMobileOpen}
            onClose={() => setIsMobileOpen(false)}
            ModalProps={{
              keepMounted: true, // Better mobile performance
            }}
            sx={{
              '& .MuiDrawer-paper': {
                width: 280,
                boxSizing: 'border-box',
                backgroundColor: theme.palette.background.default,
                zIndex: theme.zIndex.drawer + 1,
              },
            }}
          >
            {drawerContent}
          </Drawer>
        </>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: theme.zIndex.modal + 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              border: `3px solid ${theme.palette.primary.main}20`,
              borderTop: `3px solid ${theme.palette.primary.main}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          />
        </Box>
      )}
    </>
  );
});

NewSideBar.displayName = 'NewSideBar';

export default NewSideBar;
